import React, { useState } from 'react'
import { useInventoryComprehensive, useFiscalWeeks } from '../lib/data-service'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import GlobalHeader from '../components/GlobalHeader'
import { BackButton } from '../components/BackButton'
import { 
  Package, 
  TrendingUp, 
  TrendingDown, 
  Search,
  RefreshCw,
  Warehouse,
  Store,
  AlertTriangle,
  CheckCircle
} from 'lucide-react'

export default function InventoryCoverageDetail() {
  const { selectedFiscalWeek } = useFiscalWeekContext()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<'name' | 'storeWOS' | 'dcWOS' | 'totalWOS' | 'riskLevel'>('totalWOS')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Fetch inventory data
  const { data: inventory, isLoading, error, refetch } = useInventoryComprehensive(selectedFiscalWeek)
  const { data: fiscalWeeks } = useFiscalWeeks()

  const formatWOS = (value: number) => {
    return `${value.toFixed(1)}w`
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const getRiskLevel = (wos: number) => {
    if (wos < 2) return 'High'
    if (wos <= 10) return 'Medium'
    return 'Low'
  }

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'High': return 'text-red-600 dark:text-red-400'
      case 'Medium': return 'text-yellow-600 dark:text-yellow-400'
      case 'Low': return 'text-green-600 dark:text-green-400'
      default: return 'text-muted-foreground'
    }
  }

  const handleSort = (field: 'name' | 'storeWOS' | 'dcWOS' | 'totalWOS' | 'riskLevel') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection(field === 'name' ? 'asc' : 'desc')
    }
  }

  // Filter and sort inventory
  const filteredAndSortedInventory = inventory?.filter(item => 
    (item.category || item.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => {
    let aValue: number | string
    let bValue: number | string

    switch (sortField) {
      case 'name':
        aValue = a.category || a.name || ''
        bValue = b.category || b.name || ''
        break
      case 'storeWOS':
        aValue = a.wosStore || a.wos || 0
        bValue = b.wosStore || b.wos || 0
        break
      case 'dcWOS':
        aValue = a.wosDC || 0
        bValue = b.wosDC || 0
        break
      case 'totalWOS':
        aValue = a.wos || 0
        bValue = b.wos || 0
        break
      case 'riskLevel':
        const aRisk = getRiskLevel(a.wos || 0)
        const bRisk = getRiskLevel(b.wos || 0)
        aValue = aRisk === 'High' ? 3 : aRisk === 'Medium' ? 2 : 1
        bValue = bRisk === 'High' ? 3 : bRisk === 'Medium' ? 2 : 1
        break
      default:
        aValue = a.wos || 0
        bValue = b.wos || 0
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue)
    }

    const numA = typeof aValue === 'number' ? aValue : 0
    const numB = typeof bValue === 'number' ? bValue : 0
    
    return sortDirection === 'asc' ? numA - numB : numB - numA
  }) || []

  if (isLoading) {
    return (
      <div className="space-y-6">
        <GlobalHeader 
          title="Inventory Coverage Deep Dive"
          subtitle="Loading detailed inventory analysis..."
          isLoading={true}
        />
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded-lg"></div>
          <div className="h-64 bg-muted rounded-lg"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <GlobalHeader 
          title="Inventory Coverage Deep Dive"
          subtitle="Error loading inventory data"
        />
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
          <p className="text-destructive">Failed to load inventory data. Please try again.</p>
          <button 
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Check if we have any data
  if (!inventory || inventory.length === 0) {
    return (
      <div className="space-y-6">
        <GlobalHeader 
          title="Inventory Coverage Deep Dive"
          subtitle="No detailed inventory data available"
        />
        
        <div className="bg-muted/50 border border-muted rounded-lg p-8 text-center">
          <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Inventory Data Available</h3>
          <p className="text-muted-foreground mb-4">
            Detailed SKU-level inventory data is not available in the current dataset. 
            The original CSV files contain this data, but only summary data was synced to the database.
          </p>
          <BackButton href="/dashboard" label="Back to Dashboard" />
        </div>
      </div>
    )
  }

  // Calculate summary metrics
  const avgWOS = inventory?.reduce((sum, item) => sum + (item.wos || 0), 0) / (inventory?.length || 1)
  const highRiskCount = inventory?.filter(item => getRiskLevel(item.wos || 0) === 'High').length || 0
  const lowStockCount = inventory?.filter(item => (item.wos || 0) < 2).length || 0

  return (
    <div className="space-y-6">
      {/* Global Header */}
      <GlobalHeader 
        title="Inventory Coverage Deep Dive"
        subtitle="Detailed weeks of supply analysis by SKU with risk assessment"
        onRefresh={() => refetch()}
        isLoading={isLoading}
      />

      {/* Back Button */}
      <BackButton href="/dashboard" label="Back to Dashboard" className="mb-4" />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Package className="w-5 h-5 text-blue-500" />
            <div>
              <p className="text-sm text-muted-foreground">Average WOS</p>
              <p className="text-xl font-bold text-foreground">{formatWOS(avgWOS || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <div>
              <p className="text-sm text-muted-foreground">High Risk SKUs</p>
              <p className="text-xl font-bold text-foreground">{highRiskCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <TrendingDown className="w-5 h-5 text-orange-500" />
            <div>
              <p className="text-sm text-muted-foreground">Low Stock (&lt;2w)</p>
              <p className="text-xl font-bold text-foreground">{lowStockCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <div>
              <p className="text-sm text-muted-foreground">Total SKUs</p>
              <p className="text-xl font-bold text-foreground">{inventory?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Filter */}
      <div className="flex items-center space-x-2 max-w-md">
        <Search className="w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* Inventory Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Product</span>
                    {sortField === 'name' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('totalWOS')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Total WOS</span>
                    {sortField === 'totalWOS' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground">
                  EOH Value
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground">
                  Turn Rate
                </th>
                <th
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('riskLevel')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Risk Level</span>
                    {sortField === 'riskLevel' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedInventory.map((item, index) => {
                const riskLevel = getRiskLevel(item.wos || 0)
                return (
                  <tr key={item.category || item.name || `item-${index}`} className="border-b border-border hover:bg-muted/30 transition-colors">
                    <td className="p-4">
                      <div className="font-medium text-foreground">
                        {item.category || item.name || 'Unknown'}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className={`font-medium ${getRiskColor(riskLevel)}`}>
                        {formatWOS(item.wos || 0)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-foreground">
                        {formatCurrency(item.eohTotal || 0)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-foreground">
                        {item.turnRate ? `${item.turnRate.toFixed(1)}x` : '—'}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${
                        riskLevel === 'High' ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' :
                        riskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400' :
                        'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                      }`}>
                        {riskLevel === 'High' ? <AlertTriangle className="w-3 h-3" /> :
                         riskLevel === 'Medium' ? <TrendingDown className="w-3 h-3" /> :
                         <CheckCircle className="w-3 h-3" />}
                        <span>{riskLevel}</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
